import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  HomePage,
  RestaurantPage,
  CheckoutPage,
  OrderTrackingPage,
  MyOrdersPage,
  LoginPage,
  RegisterPage,
  VerifyEmailPage,
  CompleteRegistrationPage,
  TermsAcceptancePage,
  TermsPage,
  PrivacyPage,
  RestaurantPanelPage,
  AdminPanelPage,
  AddressesPage,
  ProfilePage,
  PaymentSuccessPage,
  PaymentResultPage,
  OrderConfirmationPage,
  GuestOrderTrackingPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  CourierWalletPage,
  SupportPage,
  CookiesPage,
  FaqPage,
  ContactPage,
  CareerPage,
  AboutPage,
  DeliveryReturnPage,
  DistanceSalesPage,
  AppDownloadPage,
} from '@pages/index';
import { useAuthStore } from '@store/authStore';
import { RoleProtectedRoute } from './components/hoc/RoleProtectedRoute';
import { MobileAppBanner } from '@organisms/index';

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const { user } = useAuthStore();
  const isPrivilegedRole = user?.role === 'ADMIN' || user?.role === 'RESTAURANT';

  return (
    <>
      {!isPrivilegedRole && <MobileAppBanner />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/complete-registration" element={<CompleteRegistrationPage />} />
        <Route path="/accept-terms" element={<TermsAcceptancePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/careers" element={<CareerPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/teslimat-iade" element={<DeliveryReturnPage />} />
        <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/indir" element={<AppDownloadPage />} />
        <Route path="/restaurant/:id" element={<RestaurantPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/checkout/result" element={<PaymentResultPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/confirmation" element={<OrderConfirmationPage />} />
        <Route path="/siparis-takip" element={<GuestOrderTrackingPage />} />
        
        {/* Authenticated User Routes */}
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute>
              <OrderTrackingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <MyOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/addresses"
          element={
            <ProtectedRoute>
              <AddressesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        
        {/* Restaurant Manager Dashboard */}
        <Route
          path="/restaurant-panel"
          element={
            <RoleProtectedRoute allowedRoles={['RESTAURANT', 'ADMIN']}>
              <RestaurantPanelPage />
            </RoleProtectedRoute>
          }
        />
        
        {/* Admin Dashboard */}
        <Route
          path="/admin"
          element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <AdminPanelPage />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/courier/wallet"
          element={
            <RoleProtectedRoute allowedRoles={['COURIER', 'ADMIN']}>
              <CourierWalletPage />
            </RoleProtectedRoute>
          }
        />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function HomeRoute() {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated && user?.role === 'ADMIN') {
    return <Navigate to="/admin" replace />;
  }
  if (isAuthenticated && user?.role === 'RESTAURANT') {
    return <Navigate to="/restaurant-panel" replace />;
  }

  return <HomePage />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default App;
